<template>
  <div :class="inputClasses" class="input-wrapper">
    <label :for="id" class="label">{{ label }}</label>

    <div class="input-inner">
      <input
        :id="id"
        :placeholder="placeholder"
        :required="required"
        :type="type"
        :value="props.value"
        :aria-describedby="
          !!(hasError || supportingText) ? `${id}-description` : undefined
        "
        :aria-invalid="hasError"
        autocomplete="off"
        class="input-field"
        @blur="onInputBlur"
        @focus="onInputFocus"
        @input="onInputValidate"
      />

      <div
        v-if="props.value"
        :aria-label="cancelIconAltText"
        class="clear-button"
        role="button"
        tabindex="0"
        @mousedown.prevent="clearInput"
        @keydown.enter.space="clearInput"
      >
        <CancelIcon />
      </div>
    </div>

    <BmTransition :show="!!(hasError || supportingText)">
      <p
        :id="`${id}-description`"
        :role="hasError ? 'alert' : undefined"
        class="supporting-text"
      >
        {{ hasError ? errorMessage : supportingText }}
      </p>
    </BmTransition>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import BmTransition from '@/v2/design-system/transitions/BmSlideDownTransition.vue';
import CancelIcon from '@/v2/design-system/assets/icons/CancelIcon.vue';

const props = defineProps({
  value: String,
  onInput: Function,
  placeholder: String,
  label: { type: String, default: 'Input field' },
  supportingText: String,
  required: { type: Boolean, default: false },
  type: { type: String, default: 'text' },
  rules: {
    type: Array,
    default: () => []
  },
  id: { type: String, default: 'bm-input' },
  cancelIconAltText: String
});

const hasError = ref(false);
const errorMessage = ref('');
const hasFocus = ref(false);

const inputClasses = computed(() => {
  return {
    error: hasError.value,
    focus: hasFocus.value
  };
});

const onInputValidate = event => {
  const val = event.target.value;
  validate(val);
  props.onInput?.(val);
};

const onInputFocus = () => {
  hasFocus.value = true;
};

const onInputBlur = () => {
  hasFocus.value = false;
  validate();
};

const validate = (val = props.value) => {
  hasError.value = false;
  errorMessage.value = '';

  for (const rule of props.rules) {
    const result = typeof rule === 'function' ? rule(val) : rule;
    if (result !== true) {
      hasError.value = true;
      errorMessage.value = result;
      return;
    }
  }
};

const clearInput = () => {
  props.onInput?.('');
  validate('');
};
</script>

<style scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
}

.input-inner {
  position: relative;
  display: flex;
  align-items: center;
}

.input-field {
  width: 100%;
  font-family: var(--body-small-reg-family), sans-serif;
  font-style: normal;
  font-size: var(--body-base-reg-size);
  font-weight: var(--body-small-reg-weight);
  line-height: var(--line-height-lead-sm);
  color: var(--neutral-text-icon-body);
  padding: var(--pad-space-sp-12) var(--pad-space-sp-48) var(--pad-space-sp-12)
    var(--pad-space-sp-16);
  border-radius: var(--corner-radius-c-8);
  border: var(--border-width-b-1) solid var(--neutral-border-default);
  background-color: var(--neutral-surface-subtle);
  caret-color: var(--primary-border-default);
  outline: none;
}

.input-field:hover {
  border-color: var(--neutral-border-hover);
  background-color: var(--neutral-surface-default);
}

.input-wrapper {
  &.focus {
    & .input-field {
      border-color: var(--primary-surface-default);
      outline: var(--border-width-b-1) solid var(--primary-surface-default);
    }

    & .clear-button {
      color: var(--primary-surface-default);
    }
  }

  &.error {
    & .input-field {
      outline: none;
      border-color: var(--error-surface-default);
    }

    & .supporting-text,
    .label,
    .clear-button {
      color: var(--error-surface-default);
    }
  }
}

.clear-button {
  position: absolute;
  right: var(--pad-space-sp-16);
  top: var(--pad-space-sp-14);
  cursor: pointer;
  height: var(--icon-size-i-16);
  width: var(--icon-size-i-16);
}

.label {
  color: var(--neutral-text-icon-label);
  font-family: var(--label-caption-reg-family), sans-serif;
  font-size: var(--body-small-reg-size);
  font-style: normal;
  font-weight: var(--label-caption-med-weight);
  line-height: var(--label-caption-med-line-height);
  padding-bottom: var(--pad-space-sp-8);
}

.supporting-text {
  color: var(--neutral-text-icon-label);
  font-family: var(--label-caption-reg-family), sans-serif;
  font-size: var(--body-small-reg-size);
  font-style: normal;
  font-weight: var(--label-caption-reg-weight);
  line-height: var(--label-caption-reg-line-height);
  margin-top: var(--pad-space-sp-8);
}
</style>
